<script setup lang="ts">
import { WebchatSchema } from '~/schemas/webchat';

const { data: page } = await useAsyncData(() => 
  queryCollection('WebchatPage').first()
);

useSeoMeta({
  title: page.value?.metaTitle,
  description: page.value?.metaDescription,
});

useHead({
  link: [
    { rel: "canonical", href: page.value?.linkCanonical },
  ],
  script: [
    {
      type: 'application/ld+json',
      textContent: JSON.stringify(WebchatSchema)
    },
  ],
});

const modalsStore = useModalsStore();

function handleOpenModal() {
  modalsStore.openModal('CTA');
};

function handleOnClickViewPrices() {
  const elem = document.getElementById('prices');
  elem?.scrollIntoView({ behavior: 'smooth' });
};

function handleOpenChatbox() {
  const chatboxBtn = document.getElementById('nava-chat-button') as HTMLButtonElement;
  if (!chatboxBtn) return;
  chatboxBtn.click();
};
</script>

<template>
  <template v-if="page">
    <SectionsBase class="hero-background">
      <div class="container flex flex-col items-center relative z-1">
        <div>
          <h1 class="text-primary-600 text-center highlight">{{ page.title }}</h1>
          <span class="h1"><span class="semicolon">؛</span> {{ page.subTitle }}</span>
        </div>
        <p class="text-gray-500 text-body1-semibold mt-8 text-center">{{ page.description }}</p>
        <div class="w-full flex-center flex-col lg:flex-row gap-4 mt-8 lg:mt-12">
          <Button
            :append-icon="{ name: 'solar:round-alt-arrow-left-bold' }"
            size="Large"
            pill
            @click="handleOpenModal"
            class="relative w-46.5"
          >
            <span>رایگان امتحان کنید</span>
            <IconsCurvedArrow 
              class="w-30 text-secondary-600 absolute scale-y-[-1] scale-x-[-1] origin-bottom bottom-14 right-18 hidden
                lg:block pointer-events-none" 
            />
          </Button>
          <Button
            type="Outline"
            :append-icon="{ name: 'solar:round-alt-arrow-left-bold' }"
            size="Large"
            pill
            @click="handleOnClickViewPrices"
            class="w-46.5 [&_.btn-slot-parent]:w-full [&_.btn-slot-parent]:justify-between!"
          >
            مشاهده تعرفه‌ها
          </Button>
        </div>
        <div class="w-full flex-center flex-col gap-10 lg:gap-8 mt-8 lg:mt-20">
          <div class="w-full lg:max-w-225 relative flex-center mx-auto lg:order-2">
            <NuxtImg 
              width="1350"
              height="810"
              src="/img/new/webchat/hero-laptop.webp"
              alt="چت آنلاین نواتل"
              :preload="{ fetchPriority: 'high' }"
              fetchpriority="high"
              loading="eager"
              class="w-full"
            />
            <NuxtImg
              src="/img/new/webchat/hero-phone.webp"
              alt="چت آنلاین نواتل"
              :preload="{ fetchPriority: 'high' }"
              fetchpriority="high"
              loading="eager"
              class="w-15 lg:w-50 absolute bottom-0 right-4 lg:-right-20 object-cover z-1"
            />
            <NuxtImg 
              src="/img/new/webchat/hero-chat.webp"
              alt="چت آنلاین نواتل"
              :preload="{ fetchPriority: 'high' }"
              fetchpriority="high"
              loading="eager"
              class="w-25 lg:w-60 absolute right-15 lg:right-40 top-25 lg:top-68 shadow-primary-m floating-anim-01 object-cover"
            />
            <NuxtImg
              src="/img/new/webchat/hero-message-01.webp"
              alt="چت آنلاین نواتل"
              :preload="{ fetchPriority: 'high' }"
              fetchpriority="high"
              loading="eager"
              class="w-40 lg:w-100 absolute left-0 top-22 lg:top-57 filter-[drop-shadow(0_20px_40px_rgba(0,0,0,0.25))] floating-anim-02 object-cover"
            />
            <NuxtImg 
              src="/img/new/webchat/hero-message-02.webp"
              alt="چت آنلاین نواتل"
              :preload="{ fetchPriority: 'high' }"
              fetchpriority="high"
              loading="eager"
              class="w-15 lg:w-34 absolute bottom-5 lg:bottom-14 right-0 lg:-right-29 filter-[drop-shadow(0_20px_40px_rgba(0,0,0,0.25))] floating-anim-03 object-cover z-2"
            />
          </div>
          <div class="w-full grid grid-cols-2 lg:flex lg:items-center lg:justify-center gap-4 lg:gap-10 lg:order-1">
            <div 
              v-for="item in page.items"
              :key="item"
              class="flex items-start min-[375px]:items-center lg:justify-center gap-1"
            >
              <Icon name="solar:check-circle-bold" size="24" class="text-secondary-600 min-w-6" />
              <span class="text-body2-bold text-gray-400 text-start">{{ item }}</span>
            </div>
          </div>
        </div>
      </div>
    </SectionsBase>

    <LazyIdleSectionsLogos hydrate-on-idle />

    <LazyVisibleTOC hydrate-on-visible :topics="page.topics" />

    <LazySectionsFeaturesWebchat
      :title="page.features.title"
      :desc="page.features.desc"
      :items="page.features.items"
    />
    
    <LazySectionsSolutionsCarousel
      :title="page.solutions.title"
      :desc="page.solutions.desc"
      :items="page.solutions.items"
    />

    <LazyIdleSectionsNavatelOverview
      hydrate-on-idle
      :title="page.navatelOverview.title"
      :subTitle="page.navatelOverview.subTitle"
      :desc="page.navatelOverview.desc"
      mode="webchat"
    />

    <LazyIdleSectionsLaunchSteps
      hydrate-on-idle
      :title="page.launchSteps.title"
      :desc="page.launchSteps.desc"
      :items="page.launchSteps.items"    
    />

    <LazyIdleSectionsCTAV2
      hydrate-on-idle
      :title="page.CTAv2.title" 
      :desc="page.CTAv2.desc"
      @btn-click="handleOpenChatbox"
      btn-txt="شروع گفتگو"
    />

    <LazyVisibleSectionsPricesWebchat
      hydrate-on-visible
      :title="page.prices.title"
      :desc="page.prices.desc"
    />

    <SectionsFAQ
      :title="page.FAQ.title"
      :items="page.FAQ.items"
    />

    <LazyVisibleSectionsCTA 
      hydrate-on-visible
      :title="page.CTA.title" 
      :desc="page.CTA.desc"
    />
  </template>
</template>

<style scoped>
@reference "~/assets/css/main.css";

.hero-background {
  @apply
  relative bg-linear-to-b from-secondary-100 to-transparent to-10% lg:bg-none lg:mb-10 pt-25! lg:pt-26!
}
.hero-background::before {
  @apply
  content-[''] absolute w-full h-full top-0 right-0 lg:bg-[url('/img/new/backgrounds/mesh-06.webp')] bg-no-repeat lg:bg-top 
  lg:bg-size-[100%_600px]
}

@media screen and (min-width: 1024px) {
  .floating-anim-01 {
    animation: floating-01 5s ease-in-out infinite;
  }
  @keyframes floating-01 {
    0% {
      transform: translateY(0);
    }

    50% {
      transform: translateY(-15px);
    }

    100% {
      transform: translateY(0);
    }
  }

  .floating-anim-02 {
    animation: floating-02 7s ease-in-out infinite;
  }
  @keyframes floating-02 {
    0%,
    100% {
      transform: translateY(0) rotate(-3deg);
    }

    50% {
      transform: translateY(-10px) rotate(3deg);
    }
  }

  .floating-anim-03 {
    animation: floating-03 4s ease-in-out infinite;
  }
  @keyframes floating-03 {
    0%,
    100% {
      transform: translateY(0) translateX(0) rotate(-3deg);
    }

    50% {
      transform: translateY(-10px)  translateX(10px) rotate(3deg);
    }
  } 
}
</style>